import {
  cacheExchange,
  CombinedError,
  createClient,
  dedupExchange,
  Exchange,
  fetchExchange,
  Operation,
} from 'urql';
import { fromPromise, fromValue, map, mergeMap, pipe, take } from 'wonka';
import config from '../config';
import { sec } from '../services/authService';

const GATEWAY_API_URL = config().gateway_api_url;
const AUTH0_AUDIENCE = config().auth0_audience;

export const PRODUCT = `routeManagement`;

const fetchOptionsExchange =
  (fn: any): Exchange =>
  ({ forward }) =>
  (ops$) => {
    return pipe(
      ops$,
      mergeMap((operation: Operation) => {
        const result = fn(operation.context.fetchOptions);
        return pipe(
          (typeof result.then === 'function'
            ? fromPromise(result)
            : fromValue(result)) as any,
          map((fetchOptions: RequestInit | (() => RequestInit)) => ({
            ...operation,
            context: { ...operation.context, fetchOptions },
          })),
        );
      }),
      forward,
    );
  };

// Helper function to clear all browser storage except persist:delivery
const clearAllStorage = (): void => {
  // Save persist:delivery from localStorage before clearing
  const persistDelivery = localStorage.getItem('persist:delivery');

  // Clear all localStorage and sessionStorage
  localStorage.clear();
  sessionStorage.clear();

  // Restore persist:delivery if it existed
  if (persistDelivery) {
    localStorage.setItem('persist:delivery', persistDelivery);
  }

  // Clear all cookies except persist:delivery
  document.cookie.split(';').forEach((cookie) => {
    const cookieName = cookie.split('=')[0].trim();
    if (cookieName !== 'persist:delivery') {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    }
  });
};

// getAccessTokenSilently errors meaning the cached refresh token can no longer
// mint an access token. Nothing recovers these locally: auth0-react derives
// `isAuthenticated` from the cached ID token, so the user stays "logged in"
// (ProtectedRoute never redirects, /login bounces back) while every token fetch
// fails. Only a fresh trip through Auth0 fixes it.
// Exported for tests — this is the file's core safety property and can't be
// exercised end-to-end without a genuinely revoked refresh token.
export const isInvalidSessionError = (error: any): boolean => {
  const code = String(error?.error || '').toLowerCase();
  const message = String(
    error?.message || error?.error_description || '',
  ).toLowerCase();
  return (
    code === 'invalid_grant' ||
    code === 'login_required' ||
    code === 'missing_refresh_token' ||
    code === 'consent_required' ||
    code === 'mfa_required' ||
    message.includes('invalid refresh token') ||
    message.includes('refresh token') ||
    message.includes('login required') ||
    message.includes('login_required') ||
    message.includes('missing_refresh_token') ||
    message.includes('multifactor authentication required') ||
    message.includes('mfa_required') ||
    message.includes('consent_required')
  );
};

// Guard so that when many in-flight operations fail at once we trigger a single
// recovery redirect rather than a storm of them.
let isRecoveringSession = false;

// `openUrl: false` (v2's replacement for `localOnly`) clears only Auth0's
// cached tokens without navigating to /v2/logout, leaving the SSO session alive
// for the silent re-mint below. Since recovery here is automatic, we avoid
// clearAllStorage(), which also wipes unrelated app state (remembered kiosk
// associate, saved report columns). It stays as the fallback because
// cacheLocation is "localstorage" — something has to clear the cache.
const clearAuth0Session = (): void => {
  const logout = sec.getLogout();
  if (logout) {
    logout({ openUrl: false });
  } else {
    clearAllStorage();
  }
};

// loginWithRedirect (NOT window.location = '/login') is essential: it hits Auth0
// /authorize, which re-issues tokens silently when the SSO session is alive and
// shows the hosted login when it isn't. Either way the stale token is replaced.
const recoverFromInvalidSession = (): void => {
  if (isRecoveringSession || window.location.pathname.includes('/login')) {
    return;
  }
  isRecoveringSession = true;
  clearAuth0Session();
  const loginWithRedirect = sec.getLoginWithRedirect();
  if (loginWithRedirect) {
    // Un-latch if the redirect itself fails, otherwise this tab can never
    // attempt recovery again for the rest of its life.
    Promise.resolve(
      loginWithRedirect({ appState: { returnTo: window.location.pathname } }),
    ).catch(() => {
      isRecoveringSession = false;
    });
  } else {
    window.location.href = '/login';
  }
};

// Error handling exchange to catch auth errors.
// Uses mergeMap instead of tap so that on a successful token refresh the
// original operation can be retried in-place rather than forcing a full
// page reload (window.location.reload), which was the root cause of the
// visible "map refresh" every ~5 minutes when the access token expired.
//
// Exchange ordering: errorExchange sits BEFORE fetchOptionsExchange so that
// when it retries an operation via `forward`, the retry passes through
// fetchOptionsExchange and picks up the freshly-refreshed token automatically.
const errorExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    return pipe(
      forward(ops$),
      mergeMap((result) => {
        const isRetry = (result.operation.context as any)._isAuthRetry;
        if (
          result.error &&
          isAuthError(result.error) &&
          !window.location.pathname.includes('/login') &&
          !isRefreshing &&
          !isRetry
        ) {
          isRefreshing = true;

          const retryOperation = {
            ...result.operation,
            context: {
              ...result.operation.context,
              // Bypass cache so the retry always hits the network with the new token
              requestPolicy: 'network-only' as const,
              // Prevent this retry from being retried again if it also fails
              _isAuthRetry: true,
            },
          };

          return pipe(
            fromPromise(
              sec
                .getAccessTokenSilently()({
                  audience: GATEWAY_API_URL,
                  cacheMode: 'off',
                })
                .then(() => {
                  isRefreshing = false;
                  return true;
                })
                .catch((refreshError: any) => {
                  isRefreshing = false;
                  // Only recover a genuinely dead session. Clearing state and
                  // hard-navigating to Auth0 on a transient failure (offline,
                  // TimeoutError) strands the user on a page that can't load —
                  // worse than the stuck page this fixes. Otherwise fall
                  // through and surface the original, still-retryable error.
                  if (isInvalidSessionError(refreshError)) {
                    recoverFromInvalidSession();
                  }
                  return false;
                }),
            ),
            mergeMap((didRefresh) => {
              if (didRefresh) {
                // Re-execute the operation; since errorExchange is ordered
                // before fetchOptionsExchange, this retry passes through
                // fetchOptionsExchange and gets a fresh auth token.
                return pipe(forward(fromValue(retryOperation)), take(1));
              }
              // Token refresh failed — return the original error result
              return fromValue(result);
            }),
          );
        }

        return fromValue(result);
      }),
    );
  };

// Helper to check if error is authentication related
const isAuthError = (error: CombinedError): boolean => {
  // Check for 401 Unauthorized or 403 Forbidden
  if (error.networkError) {
    const response = error.networkError as any;
    // Check various possible status code locations
    const statusCode =
      response?.statusCode || response?.status || response?.response?.status;
    if (statusCode === 401 || statusCode === 403) {
      console.error('Auth debug - Detected HTTP error:', statusCode);
      return true;
    }
  }
  // Check for GraphQL auth errors
  if (error.graphQLErrors) {
    const hasAuthError = error.graphQLErrors.some(
      (err: any) =>
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.extensions?.code === 'FORBIDDEN' ||
        err.message?.toLowerCase().includes('unauthorized') ||
        err.message?.toLowerCase().includes('unauthenticated') ||
        err.message?.toLowerCase().includes('forbidden'),
    );
    if (hasAuthError) {
      console.error('Auth debug - Detected GraphQL auth error');
    }
    return hasAuthError;
  }
  return false;
};

// Track if we've already tried to refresh the token to prevent infinite retries
let isRefreshing = false;

// The mock server keeps separate data per page load, so a reload starts fresh.
const mockSessionHeaders: Record<string, string> =
  process.env.MOCK === 'true'
    ? { 'x-mock-session': Math.random().toString(36).slice(2) }
    : {};

export const client = createClient({
  // MOCK mode posts to the same-origin /graphql path proxied to the mock
  // GraphQL server by the webpack dev server (see webpack.config.js), so the
  // app works through a single tunnel on a physical device.
  url: process.env.MOCK === 'true' ? '/graphql' : GATEWAY_API_URL,
  exchanges: [
    dedupExchange,
    cacheExchange,
    // errorExchange must come BEFORE fetchOptionsExchange so that when it
    // retries a failed operation, the retry flows through fetchOptionsExchange
    // and automatically picks up the freshly-refreshed auth token.
    errorExchange,
    fetchOptionsExchange(async (fetchOptions: any) => {
      try {
        const token =
          process.env.CYPRESS_AUTH_MOCKED === 'true'
            ? 'super_cool_token'
            : await sec.getAccessTokenSilently()({
                audience: AUTH0_AUDIENCE,
                cacheMode: 'on', // Use cached token by default
              });

        return Promise.resolve({
          ...fetchOptions,
          headers: {
            authorization: token ? `Bearer ${token}` : '',
            'apollographql-client-name': 'com.odeko.delivery-management.web',
            // TODO: add client version 'apollographql-client-version': '1.0.0'
            ...mockSessionHeaders,
          },
        });
      } catch (error) {
        console.error('Failed to get access token:', error);
        // Without this the request just goes out unauthenticated and renders a
        // broken page — nothing else redirects, since the cached ID token still
        // says "authenticated".
        if (isInvalidSessionError(error)) {
          recoverFromInvalidSession();
        }
        return Promise.resolve({
          ...fetchOptions,
          headers: {
            'apollographql-client-name': 'com.odeko.delivery-management.web',
            ...mockSessionHeaders,
          },
        });
      }
    }),
    fetchExchange,
  ],
});
